import { apiUrl, kitConfig } from "./config.js";
import { appLinks, appUrl, endpointById, limits } from "@hlt/contracts";
import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef } from "react";
// src/api/errors.ts
/** Uniform API error with stable code, field errors and the support request ID. */
var ApiError = class extends Error {
	status;
	code;
	fieldErrors;
	requestId;
	retryable;
	constructor(status, code, message, fieldErrors = [], requestId = null, retryable = false) {
		super(message);
		this.status = status;
		this.code = code;
		this.fieldErrors = fieldErrors;
		this.requestId = requestId;
		this.retryable = retryable;
	}
	fieldError(path) {
		return this.fieldErrors.find((f) => f.path === path || f.path.startsWith(`${path}.`))?.message;
	}
};
const isApiError = (error) => error instanceof ApiError;
const hasEnvelope = (body) => {
	if (!body || typeof body !== "object" || !("error" in body)) return false;
	const { error } = body;
	return typeof error === "object" && error !== null && typeof error.code === "string";
};
/**
* The error of a failed response. The HLT API answers every failure with an error envelope; an answer
* without one comes from something in between (a proxy or gateway page while the API is down or
* restarting, a captive portal), so it reads as HLT Cloud being unreachable rather than as a server error.
*/
function parseError(status, body) {
	if (!hasEnvelope(body)) return status === 429 ? new ApiError(status, "rate_limited", "Too many requests", [], null, true) : new ApiError(status, "unavailable", "HLT Cloud did not answer", [], null, true);
	const e = body.error;
	return new ApiError(status, e.code ?? "internal", e.message ?? "Something went wrong", e.fieldErrors ?? [], e.requestId ?? null, e.retryable ?? false);
}
function connectionProblem(error, online = typeof navigator === "undefined" || navigator.onLine !== false) {
	if (!isApiError(error)) return null;
	switch (error.code) {
		case "network": return online ? "unreachable" : "offline";
		case "unavailable": return "unreachable";
		case "internal": return "server";
		default: return null;
	}
}
/** The shipped sentence (`errors.*`) for each connection problem; every app shows the same words. */
const connectionMessageKeys = {
	offline: "errors.connectionOffline",
	unreachable: "errors.connectionUnreachable",
	server: "errors.connectionServer"
};
// src/api/public-ids.ts
/**
* Public identifiers seen in API responses. Workspaces, audits, findings, templates and analytics pages have a
* short random `publicId` for addresses, while the apps keep working with UUIDs. Every response passes through
* here, so a router can write `/w/<publicId>/audits/<publicId>` for a UUID and read the UUID back when the
* address is opened. Kept in this browser (bounded) so known addresses resolve offline; sign-out and account
* switches remove it with the other per-user state. Identifiers are not secrets: the server authorizes every
* request.
*/
const storageKey = "hlt.publicIds";
const publicIdPattern = /^[0-9A-Za-z]{11}$/;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const byId = /* @__PURE__ */ new Map();
const byPublicId = /* @__PURE__ */ new Map();
let loaded = false;
let saveTimer;
function load() {
	if (loaded) return;
	loaded = true;
	try {
		const raw = localStorage.getItem(storageKey);
		const pairs = raw ? JSON.parse(raw) : [];
		if (!Array.isArray(pairs)) return;
		for (const pair of pairs) if (Array.isArray(pair) && typeof pair[0] === "string" && typeof pair[1] === "string") add(pair[0], pair[1]);
	} catch {}
}
function add(id, publicId) {
	if (!uuidPattern.test(id) || !publicIdPattern.test(publicId) || byId.get(id) === publicId) return false;
	byId.delete(id);
	byId.set(id, publicId);
	byPublicId.set(publicId, id);
	return true;
}
function save() {
	saveTimer = void 0;
	try {
		const pairs = [...byId.entries()].slice(-3e3);
		localStorage.setItem(storageKey, JSON.stringify(pairs));
	} catch {}
}
const record = (value) => typeof value === "object" && value !== null;
function rememberOne(value) {
	if (!record(value)) return false;
	let changed = false;
	if (typeof value.id === "string" && typeof value.publicId === "string") changed = add(value.id, value.publicId);
	if (typeof value.workspaceId === "string" && typeof value.workspacePublicId === "string") changed = add(value.workspaceId, value.workspacePublicId) || changed;
	if (typeof value.pageId === "string" && typeof value.pagePublicId === "string") changed = add(value.pageId, value.pagePublicId) || changed;
	return changed;
}
/** Remember the identifier pairs of one response: the object itself and the rows of its lists. */
function rememberPublicIds(data) {
	if (!record(data)) return;
	load();
	let changed = rememberOne(data);
	for (const list of [
		data.items,
		data.audits,
		data.findings
	]) if (Array.isArray(list)) for (const item of list) changed = rememberOne(item) || changed;
	if (changed && typeof window !== "undefined") saveTimer ??= setTimeout(save, 500);
}
/** The public identifier of a UUID, when one was seen. */
function publicIdOf(id) {
	load();
	return byId.get(id.toLowerCase()) ?? byId.get(id) ?? null;
}
/** The UUID a public identifier stands for, when it was seen. */
function idOfPublicId(publicId) {
	load();
	return byPublicId.get(publicId) ?? null;
}
/** Forget every pair (sign-out, account switch, impersonation start or end). */
function forgetPublicIds() {
	byId.clear();
	byPublicId.clear();
	if (saveTimer) clearTimeout(saveTimer);
	saveTimer = void 0;
	try {
		localStorage.removeItem(storageKey);
	} catch {}
}
// src/api/client.ts
function buildPath(path, params = {}) {
	return path.replace(/:(\w+)/g, (_, name) => {
		const value = params[name];
		if (!value) throw new Error(`Missing path parameter ${name}`);
		return encodeURIComponent(value);
	});
}
function queryString(input) {
	if (!input || typeof input !== "object") return "";
	const search = new URLSearchParams();
	for (const [key, value] of Object.entries(input)) {
		if (value === void 0 || value === null || value === "") continue;
		search.set(key, typeof value === "object" ? JSON.stringify(value) : String(value));
	}
	const text = search.toString();
	return text ? `?${text}` : "";
}
/** Typed call of a catalog operation. Cookies carry the session; mutations get an Idempotency-Key. */
async function call(operationId, options = {}) {
	const endpoint = endpointById[operationId];
	const isQuery = endpoint.requestLocation === "query";
	const url = apiUrl(buildPath(endpoint.path, options.params) + (isQuery ? queryString(options.input) : ""));
	const headers = { accept: "application/json" };
	let body;
	if (!isQuery) {
		headers["content-type"] = "application/json";
		body = JSON.stringify(options.input ?? {});
	}
	if (endpoint.method === "POST") headers["idempotency-key"] = options.idempotencyKey ?? crypto.randomUUID();
	const timeout = options.timeoutMs === void 0 ? null : AbortSignal.timeout(options.timeoutMs);
	const signal = timeout && options.signal ? AbortSignal.any([options.signal, timeout]) : timeout ?? options.signal;
	let response;
	let text;
	try {
		response = await fetch(url, {
			method: endpoint.method,
			headers,
			body,
			credentials: "include",
			signal
		});
		text = await response.text();
	} catch (error) {
		if (typeof error === "object" && error !== null && "name" in error && error.name === "AbortError" && !timeout?.aborted) throw error;
		throw new ApiError(0, "network", "HLT Cloud could not be reached.", [], null, true);
	}
	let parsed = null;
	let unreadable = false;
	if (text) try {
		parsed = JSON.parse(text);
	} catch {
		unreadable = true;
	}
	if (!response.ok) {
		const error = parseError(response.status, parsed);
		if (error.code === "mfa_enrollment_required" && typeof window !== "undefined") {
			const setup = new URL(appUrl(kitConfig().origins, appLinks.mfaSetup()));
			if (window.location.origin !== setup.origin || window.location.pathname !== setup.pathname) {
				setup.searchParams.set("next", window.location.href);
				window.location.replace(setup.toString());
				return await new Promise(() => void 0);
			}
		}
		throw error;
	}
	if (unreadable) throw new ApiError(response.status, "unavailable", "HLT Cloud did not answer", [], null, true);
	rememberPublicIds(parsed);
	return parsed;
}
/** URL of a protected media variant (served only after a server-side visibility check). */
const mediaUrl = (organizationId, mediaId, variant = "thumbnail") => apiUrl(`${buildPath(endpointById.downloadMedia.path, {
	organizationId,
	mediaId
})}?variant=${variant}`);
/** Upload raw bytes to a reserved upload ticket path. */
async function uploadBytes(uploadPath, blob, mime, onProgress) {
	return await new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open("PUT", apiUrl(uploadPath));
		xhr.setRequestHeader("content-type", mime);
		xhr.withCredentials = true;
		xhr.upload.onprogress = (event) => {
			if (event.lengthComputable) onProgress?.(event.loaded / event.total);
		};
		xhr.onload = () => {
			let parsed = null;
			try {
				parsed = JSON.parse(xhr.responseText);
			} catch {
				parsed = null;
			}
			if (xhr.status >= 200 && xhr.status < 300) resolve(parsed);
			else reject(parseError(xhr.status, parsed));
		};
		xhr.onerror = () => reject(new ApiError(0, "network", "Upload interrupted. Retry when you are back online.", [], null, true));
		xhr.send(blob);
	});
}
// src/api/query.ts
/**
* Defaults for every app. Data counts as fresh for 30 s, so moving between subpages and back renders from the
* cache; older data refetches on mount and on window focus, and mutations invalidate what they change. Unused
* results stay cached for 10 minutes. Queries whose freshness matters more (session, branding) set their own
* `staleTime`.
*/
function createQueryClient() {
	return new QueryClient({ defaultOptions: {
		queries: {
			staleTime: 3e4,
			gcTime: 6e5,
			refetchOnWindowFocus: true,
			retry: (count, error) => error instanceof ApiError && error.retryable && count < 2
		},
		mutations: { retry: false }
	} });
}
/**
* Query keys include the operation, path scope (organization/workspace) and input so a scope
* switch never shows another tenant's cached records. Sign-out clears the whole cache.
*/
const apiKey = (operationId, params, input) => [
	"api",
	params?.organizationId ?? null,
	params?.workspaceId ?? null,
	operationId,
	params ?? {},
	input ?? {}
];
function apiQueryOptions(operationId, options = {}) {
	return {
		queryKey: apiKey(operationId, options.params, options.input),
		queryFn: async ({ signal }) => await call(operationId, {
			...options,
			signal
		})
	};
}
function useApiQuery(operationId, options = {}, extra = {}) {
	return useQuery({
		...apiQueryOptions(operationId, options),
		...extra
	});
}
/** Mutation bound to an operation; `invalidate` lists operations whose cached reads become stale. */
function useApiMutation(operationId, config = {}) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (vars) => call(operationId, {
			params: {
				...config.params,
				...vars.params
			},
			input: vars.input,
			idempotencyKey: vars.idempotencyKey
		}),
		onSuccess: async (data) => {
			await Promise.all((config.invalidate ?? []).map((op) => client.invalidateQueries({ predicate: (q) => q.queryKey[0] === "api" && q.queryKey[3] === op })));
			config.onSuccess?.(data);
		}
	});
}
// src/api/idempotency.ts
/**
* Idempotency key for a create request that may be retried after a lost response. The same input
* reuses the key (so the server returns the original result); changed input or a settled request
* gets a fresh one, so a retry never replays a different request.
*/
function useIdempotencyKey() {
	const current = useRef(null);
	return useMemo(() => ({
		keyFor(input) {
			const fingerprint = JSON.stringify(input);
			if (current.current?.fingerprint !== fingerprint) current.current = {
				key: crypto.randomUUID(),
				fingerprint
			};
			return current.current.key;
		},
		reset() {
			current.current = null;
		}
	}), []);
}
// src/api/image-upload.ts
/** Largest picked file that is decoded on the device; bigger files are refused before any work. */
const maxSourceBytes = 41943040;
/** Decoding bound (pixels) so a hostile or panoramic image cannot exhaust a phone's memory. */
const maxSourcePixels = 8e7;
/** JPEG qualities tried in order until the image fits its byte budget. */
const qualitySteps = [
	.86,
	.78,
	.7,
	.62,
	.54,
	.46,
	.38
];
/** When even the lowest quality is too large, the long edge shrinks by this factor and the steps repeat. */
const shrinkFactor = .75;
/** Below this long edge a photo no longer serves as evidence; such a file is refused instead. */
const minEvidenceEdge = 640;
var ImageError = class extends Error {
	problem;
	constructor(problem) {
		super(problem);
		this.problem = problem;
	}
};
/**
* The encodings tried for an image, best first: the largest allowed long edge (or the image's own, if
* smaller) at falling quality, then the same steps at a smaller size, down to the minimum useful size.
*/
function encodeAttempts(sourceLongEdge, options = {}) {
	const { maxEdge = limits.evidenceLongEdge, minEdge = minEvidenceEdge, qualities = qualitySteps } = options;
	const attempts = [];
	let edge = Math.max(1, Math.min(Math.round(sourceLongEdge), maxEdge));
	for (;;) {
		for (const quality of qualities) attempts.push({
			longEdge: edge,
			quality
		});
		if (edge <= minEdge) break;
		edge = Math.max(minEdge, Math.round(edge * shrinkFactor));
	}
	return attempts;
}
/**
* Encode attempt by attempt until the result fits `maxBytes`. The first fitting result wins; if none fits,
* the image is refused rather than sent.
*/
async function compressToBudget(sourceLongEdge, maxBytes, encode, options = {}) {
	for (const attempt of encodeAttempts(sourceLongEdge, options)) {
		const blob = await encode(attempt);
		if (blob.size <= maxBytes) return blob;
	}
	throw new ImageError("too_large");
}
/**
* Size and format per kind, matching the server's upload rules: evidence and page photos as JPEG; logos (and
* page graphics that were PNG) as PNG, which keeps transparency for light and dark sidebars.
*/
function imageProfile(kind, sourceMime) {
	const preview = {
		longEdge: limits.thumbnailLongEdge,
		maxBytes: limits.thumbnailMaxBytes,
		minEdge: 64
	};
	if (kind === "logo") return {
		mime: "image/png",
		image: {
			longEdge: limits.logoLongEdge,
			maxBytes: limits.logoUploadBytes,
			minEdge: 64
		},
		preview: {
			...preview,
			longEdge: Math.round(limits.logoLongEdge / 2)
		}
	};
	if (kind === "page") return {
		mime: sourceMime === "image/png" ? "image/png" : "image/jpeg",
		image: {
			longEdge: limits.evidenceLongEdge,
			maxBytes: limits.imageUploadBytes,
			minEdge: 320
		},
		preview
	};
	return {
		mime: "image/jpeg",
		image: {
			longEdge: limits.evidenceLongEdge,
			maxBytes: limits.evidenceMaxBytes,
			minEdge: minEvidenceEdge
		},
		preview
	};
}
async function decode(file) {
	if (file.size > maxSourceBytes) throw new ImageError("too_large");
	let bitmap;
	try {
		bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
	} catch {
		throw new ImageError("unreadable");
	}
	if (bitmap.width * bitmap.height > maxSourcePixels) {
		bitmap.close();
		throw new ImageError("too_large");
	}
	return bitmap;
}
/** Draw the bitmap at `longEdge` and encode it; JPEG gets a white background instead of black transparency. */
function renderer(bitmap, mime) {
	const canvas = document.createElement("canvas");
	const sourceLongEdge = Math.max(bitmap.width, bitmap.height);
	let drawnEdge = 0;
	const encode = async ({ longEdge, quality }) => {
		if (longEdge !== drawnEdge) {
			const scale = Math.min(1, longEdge / sourceLongEdge);
			canvas.width = Math.max(1, Math.round(bitmap.width * scale));
			canvas.height = Math.max(1, Math.round(bitmap.height * scale));
			const context = canvas.getContext("2d");
			if (!context) throw new ImageError("encode_failed");
			if (mime === "image/jpeg") {
				context.fillStyle = "#ffffff";
				context.fillRect(0, 0, canvas.width, canvas.height);
			}
			context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
			drawnEdge = longEdge;
		}
		const encoded = await new Promise((resolve) => canvas.toBlob(resolve, mime, quality));
		if (!encoded) throw new ImageError("encode_failed");
		return encoded;
	};
	const release = () => {
		canvas.width = 0;
		canvas.height = 0;
	};
	return {
		encode,
		release,
		sourceLongEdge
	};
}
/** Prepare an image and its preview for upload. There is no fallback to the original bytes. */
async function prepareImage(file, kind) {
	const bitmap = await decode(file);
	const profile = imageProfile(kind, file.type);
	const qualities = profile.mime === "image/png" ? [1] : qualitySteps;
	const { encode, release, sourceLongEdge } = renderer(bitmap, profile.mime);
	try {
		const fit = async (target) => await compressToBudget(sourceLongEdge, target.maxBytes, encode, {
			maxEdge: target.longEdge,
			minEdge: Math.min(target.minEdge, target.longEdge),
			qualities
		});
		const image = await fit(profile.image);
		const preview = await fit(profile.preview);
		return {
			body: new Blob([image, preview], { type: profile.mime }),
			mime: profile.mime,
			bytes: image.size,
			thumbnailBytes: preview.size
		};
	} finally {
		release();
		bitmap.close();
	}
}
/** Profile pictures: center-cropped to a square of this size, JPEG, no preview (the picture is small). */
const avatarSize = 512;
/** Crop to a centered square and scale to `avatarSize` px as JPEG, so no cropping UI is needed. */
async function prepareAvatar(file) {
	const bitmap = await decode(file);
	try {
		const side = Math.min(bitmap.width, bitmap.height);
		const edge = Math.min(avatarSize, side);
		const canvas = document.createElement("canvas");
		canvas.width = edge;
		canvas.height = edge;
		const context = canvas.getContext("2d");
		if (!context) throw new ImageError("encode_failed");
		context.fillStyle = "#ffffff";
		context.fillRect(0, 0, edge, edge);
		context.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, edge, edge);
		const blob = await compressToBudget(edge, limits.avatarMaxBytes, async ({ quality }) => {
			const encoded = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
			if (!encoded) throw new ImageError("encode_failed");
			return encoded;
		}, {
			maxEdge: edge,
			minEdge: edge
		});
		canvas.width = 0;
		canvas.height = 0;
		return blob;
	} finally {
		bitmap.close();
	}
}
/**
* Prepare, reserve and upload one image for a media owner. The server checks and stores it in the same
* request, so the returned item is ready (or the call fails).
*/
async function uploadImage(input) {
	input.onPreparing?.();
	const prepared = await prepareImage(input.file, input.kind);
	return await uploadBytes((await call("reserveUpload", {
		params: { organizationId: input.organizationId },
		input: {
			ownerType: input.ownerType,
			ownerId: input.ownerId,
			mime: prepared.mime,
			bytes: prepared.bytes,
			thumbnailBytes: prepared.thumbnailBytes
		}
	})).uploadPath, prepared.body, prepared.mime, input.onProgress);
}
export { ApiError, ImageError, apiKey, apiQueryOptions, buildPath, call, compressToBudget, connectionMessageKeys, connectionProblem, createQueryClient, encodeAttempts, forgetPublicIds, idOfPublicId, imageProfile, isApiError, mediaUrl, parseError, prepareAvatar, publicIdOf, qualitySteps, rememberPublicIds, uploadBytes, uploadImage, useApiMutation, useApiQuery, useIdempotencyKey };

//# sourceMappingURL=api.js.map